// Deterministic illustrative basil, not measured plant morphology or growth.
// Keep the previous leaf's extremal vertices: more surface detail must not move
// the plant envelope, the nearby cables or the canopy-referenced Chaac height.
const BASE_ROWS = 10;
export const basilLeafResolution = Object.freeze({ rows: 40, columns: 12, variants: 3 });

function originalVertex(THREE, row, side) {
  const t = row / BASE_ROWS, wave = Math.sin(Math.PI * t);
  return new THREE.Vector3(side * .43 * Math.pow(wave, .85), .10 * wave - .09 * t * t - Math.abs(side) * .055 * wave, t);
}

export function createBasilLeafGeometry(THREE, variant = 0) {
  const { rows, columns } = basilLeafResolution, positions = [], colors = [], indices = [];
  const green = new THREE.Color(0x3b752c), vein = new THREE.Color(0x65a247), shade = new THREE.Color(0x2f6221);
  const phase = ((variant % 3) + 3) % 3;
  for (let row = 0; row <= rows; row += 1) {
    const t = row / rows, base = Math.min(BASE_ROWS - 1, Math.floor(t * BASE_ROWS)), fraction = t * BASE_ROWS - base;
    const scallop = Math.pow(Math.sin(Math.PI * fraction), 2);
    for (let column = 0; column <= columns; column += 1) {
      const side = 2 * column / columns - 1, direction = side < 0 ? -1 : 1;
      // All positions are convex combinations of existing vertices. Small
      // inward scallops refine the broad ovate outline without serrated lobes.
      const inset = 1 - scallop * (.025 + .012 * Math.sin(t * Math.PI * 8 + phase + direction));
      const amount = Math.abs(side) * inset;
      const low = originalVertex(THREE, base, 0).lerp(originalVertex(THREE, base, direction), amount);
      const high = originalVertex(THREE, base + 1, 0).lerp(originalVertex(THREE, base + 1, direction), amount);
      const point = low.lerp(high, fraction);
      positions.push(point.x, point.y, point.z);

      // A continuous central vein and six curved pairs of secondary veins are
      // vertex colours on the same surface, not hundreds of extra objects.
      const midrib = Math.exp(-Math.pow(side / .095, 2)) * Math.sin(Math.PI * t);
      let secondary = 0;
      for (let pair = 0; pair < 6; pair += 1) {
        const at = .15 + pair * .115 + Math.abs(side) * (.14 - pair * .012);
        const distance = Math.abs(t - at);
        secondary = Math.max(secondary, Math.exp(-Math.pow(distance / .014, 2)) * (1 - Math.abs(side) * .5));
      }
      const surface = green.clone().lerp(shade, .12 + .14 * Math.abs(side));
      surface.lerp(vein, Math.min(.72, midrib * .62 + secondary * .34));
      surface.multiplyScalar(.96 + phase * .025 + .035 * Math.sin(Math.PI * t));
      colors.push(surface.r, surface.g, surface.b);
      if (row < rows && column < columns) {
        const a = row * (columns + 1) + column, b = a + columns + 1;
        indices.push(a, b, a + 1, a + 1, b, b + 1);
      }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  geometry.userData = { morphology: "illustrative-broad-ovate-basil", envelope: "existing-leaf-convex-envelope", variant: phase, veinPairs: 6 };
  return geometry;
}

export function createBasilPlantAssets(THREE, { material }) {
  return {
    leafGeometries: Array.from({ length: 3 }, (_, index) => createBasilLeafGeometry(THREE, index)),
    leafMaterials: [0xffffff, 0xf1f6e8, 0xf7ffe9].map(color => material(color, { vertexColors: true, side: THREE.DoubleSide, roughness: .7, metalness: 0 })),
    stemMaterial: material(0x4d8839, { roughness: .78, metalness: 0 })
  };
}

export function basilBranchAnchorAtHeight(curve, height) {
  if (!Number.isFinite(height) || height < curve.getPoint(0).y || height > curve.getPoint(1).y) throw new RangeError("BASIL_BRANCH_HEIGHT");
  let low = 0, high = 1;
  // These branch controls increase monotonically in Y; no nearest-point guess
  // or movement of the retained leaf position is required.
  for (let step = 0; step < 45; step += 1) {
    const middle = (low + high) / 2;
    if (curve.getPoint(middle).y < height) low = middle; else high = middle;
  }
  return curve.getPoint((low + high) / 2);
}

export function createBasilBranch(THREE, { mesh, material, parent, start, end, radius, bend }) {
  const a = new THREE.Vector3(...start), b = new THREE.Vector3(...end), offset = new THREE.Vector3(...bend);
  const curve = new THREE.CubicBezierCurve3(a, a.clone().lerp(b, .3).add(offset), a.clone().lerp(b, .7).addScaledVector(offset, -.4), b);
  const segments = 12, sides = 7, geometry = new THREE.TubeGeometry(curve, segments, 1, sides, false);
  const positions = geometry.attributes.position;
  for (let ring = 0; ring <= segments; ring += 1) {
    const center = curve.getPointAt(ring / segments), width = radius * (1 - .55 * ring / segments);
    for (let side = 0; side <= sides; side += 1) {
      const index = ring * (sides + 1) + side;
      positions.setXYZ(index, center.x + (positions.getX(index) - center.x) * width, center.y + (positions.getY(index) - center.y) * width, center.z + (positions.getZ(index) - center.z) * width);
    }
  }
  geometry.computeVertexNormals();
  const object = mesh(geometry, material, parent); object.name = "basil-curved-tapered-branch";
  object.userData.evidence = "illustrative-branch-not-growth-simulation";
  return { object, curve, radius };
}
